import { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

const HISTORY_ERROR =
  "Το ακριβές ιστορικό δεν ήταν διαθέσιμο. Προβάλλονται μόνο οι κλασικές ενημερώσεις.";

// Loads a single Αστικά case with its classic updates and its history.
// Refetches only when caseId or refreshKey change. Only an auth failure
// redirects to login; other errors are returned so callers can show them.
export default function useCaseDetail(caseId, { refreshKey } = {}) {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState({
    data: undefined,
    updates: [],
    history: undefined,
    historyError: "",
    loading: Boolean(caseId),
    error: null,
  });

  useEffect(() => {
    if (!caseId) {
      setState({
        data: undefined,
        updates: [],
        history: undefined,
        historyError: "",
        loading: false,
        error: null,
      });
      return undefined;
    }

    let isMounted = true;
    const controller = new AbortController();

    const getCase = async () => {
      // Keep showing the previous data while a background refresh runs
      // for the same case; reset when switching to another case.
      setState((prev) =>
        prev.data && String(prev.data.id) === String(caseId)
          ? { ...prev, error: null }
          : {
              data: undefined,
              updates: [],
              history: undefined,
              historyError: "",
              loading: true,
              error: null,
            }
      );

      try {
        const response = await axiosPrivate.get(`/cases/${caseId}`, {
          signal: controller.signal,
        });
        const [updatesResult, historyResult] = await Promise.allSettled([
          axiosPrivate.get(`/gakupd/${caseId}`, { signal: controller.signal }),
          axiosPrivate.get(`/cases/${caseId}/history`, {
            signal: controller.signal,
          }),
        ]);

        if (!isMounted) return;

        if (historyResult.status !== "fulfilled") {
          console.error(historyResult.reason);
        }

        setState({
          data: response.data,
          updates:
            updatesResult.status === "fulfilled" &&
            Array.isArray(updatesResult.value?.data)
              ? updatesResult.value.data
              : [],
          history:
            historyResult.status === "fulfilled"
              ? historyResult.value?.data?.data ?? null
              : null,
          historyError: historyResult.status === "fulfilled" ? "" : HISTORY_ERROR,
          loading: false,
          error: null,
        });
      } catch (err) {
        if (!isMounted || axios.isCancel(err)) return;
        console.error(err);
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          navigate("/login", { state: { from: location }, replace: true });
          return;
        }
        setState((prev) => ({ ...prev, loading: false, error: err }));
      }
    };

    getCase();

    return () => {
      isMounted = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, refreshKey, axiosPrivate]);

  return state;
}
